import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { getBillingFrequencyFormat } from "utils/getBillingFrequencyFormat";
import { getCurrency } from "utils/getCurrency";
import { getFormattedPrice } from "utils/getFormattedPrice";
import { getImageUrl } from "utils/getImageUrl";

export const COMPARE_STATUS = {
  EXISTS: "exists",
  CAPACITY_REACHED: "capacity_reached",
  ADDED: "added",
  REMOVED: "removed",
};

const CompareContext = createContext({
  compareData: [],
  isCompareVisible: false,
  maxCompareCount: 4,
  addCompareData: () => {},
  removeCompareData: () => {},
  clearCompareData: () => {},
  setCompareVisible: () => {},
  isAddedToCompare: () => {},
});

export const formatCompareData = (data) => {
  const isRoomData = !!data?.room_type;
  let formattedId;
  if (isRoomData) {
    formattedId = `room-${
      data?.property?.slug ||
      data?.property_slug ||
      data?.propertySlug ||
      data?.id
    }-${data?.slug}`;
  } else {
    formattedId = `property-${data?.slug}`;
  }
  return {
    ...data,
    image: getImageUrl(
      (data?.media_gallery || data?.media)?.[0]?.formats,
      "small",
      (data?.media_gallery || data?.media)?.[0]?.url
    ),
    type: isRoomData ? "room" : "property",
    display_name: data?.display_name || data?.name,
    formattedId,
    id: data?._id || data?.id,
    sub_text: data?.bed_type,
    price: getFormattedPrice(data?.room_starting_price),
    billing_frequency: getBillingFrequencyFormat(
      data?.room_starting_price_billing_frequency
    ),
    propertySlug:
      data?.property?.slug || data?.property_slug || data?.propertySlug,
    currency: getCurrency(data?.room_starting_price_currency),
  };
};

export const CompareProvider = ({ children }) => {
  const [compareData, setCompareData] = useState([
    ...(JSON.parse(localStorage.getItem("compareData")) ?? []),
  ]);

  const [isCompareVisible, setIsCompareVisible] = useState(false);
  const MAX_COMPARE_COUNT = window.innerWidth < 768 ? 2 : 4;

  const addCompareData = useCallback(
    (data) => {
      const formattedData = formatCompareData(data);
      let res;
      let finalArrayLength = 0;

      setCompareData((prev) => {
        const newCompareData = [...prev];
        const existingIndex = prev.findIndex(
          (p) => p?.formattedId === formattedData?.formattedId
        );

        // If item exists, remove it (toggle off)
        if (existingIndex !== -1) {
          newCompareData.splice(existingIndex, 1);
          res = COMPARE_STATUS.REMOVED;
        }
        // If capacity reached, don't add
        else if (prev.length >= MAX_COMPARE_COUNT) {
          res = COMPARE_STATUS.CAPACITY_REACHED;
          return prev; // Return unchanged array
        }
        // Otherwise add the item
        else {
          res = COMPARE_STATUS.ADDED;
          newCompareData.push(formattedData);
        }

        finalArrayLength = newCompareData.length;
        localStorage.setItem("compareData", JSON.stringify(newCompareData));
        return newCompareData;
      });

      // Automatically hide strip if array becomes empty after removal
      if (res === COMPARE_STATUS.REMOVED && finalArrayLength === 0) {
        setIsCompareVisible(false);
      }

      return res;
    },
    [MAX_COMPARE_COUNT]
  );

  const removeCompareData = useCallback((id) => {
    setCompareData((prev) => {
      let newCompareData = [...prev];
      newCompareData = newCompareData.filter((p) => p?.formattedId !== id);
      localStorage.setItem("compareData", JSON.stringify(newCompareData));
      return newCompareData;
    });
  }, []);

  const clearCompareData = useCallback(() => {
    setCompareData([]);
    localStorage.setItem("compareData", JSON.stringify([]));
  }, []);

  const setCompareVisible = useCallback((visibility) => {
    setIsCompareVisible(visibility);
  }, []);

  const isAddedToCompare = useCallback(
    (data) => {
      const formattedData = formatCompareData(data);
      return compareData.some(
        (p) => p?.formattedId === formattedData?.formattedId
      );
    },
    [compareData]
  );

  const value = useMemo(
    () => ({
      compareData,
      isCompareVisible,
      maxCompareCount: MAX_COMPARE_COUNT,
      addCompareData,
      removeCompareData,
      clearCompareData,
      setCompareVisible,
      isAddedToCompare,
    }),
    [
      compareData,
      isCompareVisible,
      MAX_COMPARE_COUNT,
      addCompareData,
      removeCompareData,
      clearCompareData,
      setCompareVisible,
      isAddedToCompare,
    ]
  );

  return (
    <CompareContext.Provider value={value}>{children}</CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within a CompareProvider");
  }
  return context;
};
